import { Controller } from '@hotwired/stimulus'

/**
 * Monitors a scrollable area and shows/hides a button based on
 * scroll position relative to the end of the area.
 *
 * Targets:
 *   - button: When the end of the scrollable area has not been reached,
 *     clicking it pages down. Once at the end, clicking it scrolls back to top.
 */
export default class OverflowArrowController extends Controller<HTMLElement> {
  declare readonly buttonTarget: HTMLButtonElement

  static targets = ['button']

  // Variables

  #atEnd = false
  #endPoint: number | null = null
  #pageSize = 0.66 // Fraction of the visible area to scroll per button click
  #resizeObserver: ResizeObserver | null = null
  #threshold = 2 // Pixel tolerance for considering the endpoint "reached"
  #throttleTimer = 0 // Timer ID for throttling scroll/resize updates

  // Lifecycle methods

  connect() {
    this.#updateVisibility()
    this.#observeResize()
    this.element.addEventListener('scroll', this.#handleScroll)
  }

  disconnect() {
    this.#resizeObserver?.disconnect()
    this.element.removeEventListener('scroll', this.#handleScroll)
  }

  buttonTargetConnected(element: HTMLButtonElement) {
    element.addEventListener('click', this.#handleButtonClick)
  }

  buttonTargetDisconnected(element: HTMLButtonElement) {
    element.removeEventListener('click', this.#handleButtonClick)
  }

  // Private methods

  #handleButtonClick = () => {
    if (this.#atEnd) {
      this.#scrollToTop()
    } else {
      this.#pageDown()
    }
  }

  #handleScroll = () => {
    this.#throttle(() => this.#updateVisibility(), 100)
  }

  #pageDown() {
    const offset = this.element.clientHeight * this.#pageSize
    this.element.scrollBy({ top: offset, behavior: 'smooth' })
  }

  #scrollToTop() {
    this.element.scrollTo({ top: 0, behavior: 'smooth' })
  }

  #updateVisibility() {
    const hasOverflow = this.element.scrollHeight > this.element.clientHeight
    this.buttonTarget.classList.toggle('tw-hidden', !hasOverflow)
    if (!hasOverflow) return

    const endPoint = this.#resolvedEndPoint
    const scrollTop = this.element.scrollTop
    this.#atEnd = scrollTop >= endPoint - this.#threshold

    this.buttonTarget.dataset.tooltipText = this.#atEnd ? 'Back to Top' : 'View More'
    this.buttonTarget.classList.toggle('back-to-top', this.#atEnd)
  }

  get #resolvedEndPoint(): number {
    if (this.#endPoint !== null && this.#endPoint > 0) {
      return this.#endPoint
    }
    return Math.max(0, this.element.scrollHeight - this.element.clientHeight)
  }

  #observeResize() {
    this.#resizeObserver = new ResizeObserver(() => {
      this.#throttle(() => this.#updateVisibility(), 100)
    })
    this.#resizeObserver.observe(this.element)
  }

  #throttle(fn: () => void, delay: number) {
    clearTimeout(this.#throttleTimer)
    this.#throttleTimer = window.setTimeout(fn, delay)
  }
}
